// src/components/Constructor.js
import { useAppContext } from "../../context/AppContext";
import {
  ButtonsTrans,
  ConstructorTrans,
  TagListTrans,
  WorkoutTrans,
} from "../../utils/translations";
import bgImg from "../../assets/images/bgImg-4.png";
import DropdownButton from "../DropdownButton";
import { useState, useMemo, useEffect } from "react";
import TagList from "../TagList";
import VideoDisplay from "../VideoDisplay";
import { assemblePractice } from "../../services/practiceService";
import YouTubeVideoDisplay from "../YouTubeVideoDisplay";
import { motion, AnimatePresence } from "framer-motion";
import SaveForLaterButton from "../SaveForLaterButton";
import MarkAsCompleteButton from "../MarkAsCompleteButton";

export default function Constructor() {
  const { language, user, selectionStates, setSelectionStates, handleRestrictedAction, toggleSaved, toggleComplete, premiumRoles, token } = useAppContext();

  const [selectedTags, setSelectedTags] = useState([]);
  const [constructedPracticeLink, setConstructedPracticeLink] = useState(null);
  const [practiceUuid, setPracticeUuid] = useState("");
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFilterEmpty, setIsFilterEmpty] = useState(true);
  const [isBuilt, setIsBuilt] = useState(false);

  const tagList = useMemo(
    () => [
      { id: "balance", label: TagListTrans[language].balance },
      { id: "lotus", label: TagListTrans[language].lotus },
      { id: "hipOpeners", label: TagListTrans[language].hipOpeners },
      { id: "shavasana", label: TagListTrans[language].guidedShavasana },
      { id: "breathing", label: TagListTrans[language].breathing },
      { id: "legStrength", label: TagListTrans[language].legStrength },
      { id: "armStrength", label: TagListTrans[language].armStrength },
      { id: "flexibility", label: TagListTrans[language].flexibility },
      { id: "backStrength", label: TagListTrans[language].backStrength },
      { id: "handstands", label: TagListTrans[language].handstands },
      { id: "core", label: TagListTrans[language].core },
      { id: "armBalances", label: TagListTrans[language].armBalances },
      { id: "backBends", label: TagListTrans[language].backBends },
      { id: "splits", label: TagListTrans[language].splits },
      { id: "twists", label: TagListTrans[language].twists },
      { id: "surya", label: TagListTrans[language].surya },
    ],
    [language]
  );

  const levelOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "beginner", name: WorkoutTrans[language].beginner },
      { key: "experienced", name: WorkoutTrans[language].experienced },
      { key: "advanced", name: WorkoutTrans[language].advanced },
    ],
    [language]
  );
  const typeOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "recovery", name: WorkoutTrans[language].recovery },
      { key: "progression", name: WorkoutTrans[language].progression },
    ],
    [language]
  );
  const durationOptions = useMemo(
    () => [
      { key: null, name: WorkoutTrans[language].anyFem },
      { key: 15, name: WorkoutTrans[language].fifteen },
      { key: 30, name: WorkoutTrans[language].thirty },
      { key: 60, name: WorkoutTrans[language].sixty },
      { key: 90, name: WorkoutTrans[language].ninety },
    ],
    [language]
  );

  const [selectedLevel, setSelectedLevel] = useState(levelOptions[0]);
  const [selectedType, setSelectedType] = useState(typeOptions[0]);
  const [selectedDuration, setSelectedDuration] = useState(
    durationOptions[0]
  );

  const handleSearch = async () => {
    try {
      const response = await assemblePractice({
        level: selectedLevel.key,
        type: selectedType.key,
        duration: selectedDuration.key,
        tags: selectedTags,
      }, token);
      // 1) Tags: все теги без повторов
      if (!selectedTags.length) {
        const allTags = Array.from(new Set(response.flatMap(p => p.tags)));
        setSelectedTags(allTags);
      }

      // 2) Level: наиболее частый уровень среди кусков
      if (!selectedLevel) {
        const levelCount = {};
        response.forEach(p => p.level.forEach(l => {
          levelCount[l] = (levelCount[l] || 0) + 1;
        }));
        const mostLevel = Object.entries(levelCount)
          .sort((a, b) => b[1] - a[1])[0]?.[0] || "";
        setSelectedLevel(
          levelOptions.find(o => o.key === mostLevel) || levelOptions[0]
        );
      }

      // 3) Type: наиболее частый тип
      if (!selectedType) {
        const typeCount = {};
        response.forEach(p => p.type.forEach(t => {
          typeCount[t] = (typeCount[t] || 0) + 1;
        }));
        const mostType = Object.entries(typeCount)
          .sort((a, b) => b[1] - a[1])[0]?.[0] || "";
        setSelectedType(
          typeOptions.find(o => o.key === mostType) || typeOptions[0]
        );
      }

      // 4) Duration: полная длительность всех кусков
      const totalTime = response.reduce((sum, p) => sum + p.duration, 0);
      setSelectedDuration({ key: totalTime, name: `${totalTime} мин` });
      setFilteredVideos(response);
      setCurrentIndex(0);
      setConstructedPracticeLink(
        response.length > 0 ? response[0].videoURL : null
      );
      setIsBuilt(true);
      setPracticeUuid(response
        .map(piece => piece.uuid)
        .join(","));
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  const handleVideoEnd = () => {
    setCurrentIndex((i) => {
      const next = i + 1;
      if (next < filteredVideos.length) {
        setConstructedPracticeLink(filteredVideos[next].videoURL);
        return next;
      }
      return i;
    });
  };

  const handlePrev = () => {
    setCurrentIndex((i) => {
      const next = i - 1;
      if (next < filteredVideos.length) {
        setConstructedPracticeLink(filteredVideos[next].videoURL);
        return next;
      }
      return i;
    });
  };

  const handleReset = () => {
    setSelectedTags([]);
    setSelectedLevel(levelOptions[0]);
    setSelectedType(typeOptions[0]);
    setSelectedDuration(durationOptions[0]);
    setFilteredVideos([]);
    setCurrentIndex(0);
    setIsBuilt(false);
    setConstructedPracticeLink(null);
  };

  useEffect(() => {
    if (user && !premiumRoles.includes(user?.role)) {
      window.location.assign("/membersOnly")
    }
    const searchObject = {
      level: selectedLevel.key,
      type: selectedType.key,
      duration: selectedDuration.key,
      tags: selectedTags,
    };
    const allEmpty = Object.values(searchObject).every(
      (value) =>
        value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
    );
    setIsFilterEmpty(allEmpty);
    if (user) {
      setSelectionStates({
        isComplete: user?.completedPractices?.includes(practiceUuid) || false,
        isSaved: user?.savedPractices?.includes(practiceUuid) || false,
      });
    }
  }, [user, selectedLevel, selectedType, selectedDuration, selectedTags]);

  return (
    <main className="flex min-h-screen flex-col justify-between mt-5 mb-10 border-t-[1.5px] lg:border-black">
      <div className="flex-1 grid md:grid-cols-3 md:w-10/12 w-11/12 pt-10 mx-auto gap-10">
        <div className="col-span-2 flex flex-col">
          <h2 className="md:text-4xl mb-6 text-2xl md:text-left text-center">{isBuilt ? ConstructorTrans[language].workoutReady : ConstructorTrans[language].header}</h2>
          <AnimatePresence>
            {!isBuilt && (
              <motion.div
                key="selectors"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex flex-wrap justify-start gap-8 mt-5 md:text-base text-sm">
                  <DropdownButton
                    title={WorkoutTrans[language].level}
                    options={levelOptions}
                    selectedOption={selectedLevel}
                    setSelectedOption={setSelectedLevel}
                  />
                  <DropdownButton
                    title={WorkoutTrans[language].type}
                    options={typeOptions}
                    selectedOption={selectedType}
                    setSelectedOption={setSelectedType}
                  />
                  <DropdownButton
                    title={WorkoutTrans[language].duration}
                    options={durationOptions}
                    selectedOption={selectedDuration}
                    setSelectedOption={setSelectedDuration}
                  />
                </div>

                <TagList
                  tags={tagList}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                />
              </motion.div>
            )}

            {isBuilt && (
              <motion.div
                key="summary"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-white/70 p-4 rounded-md shadow-lg text-sm"
              >
                <p className="text-xs mb-3">{ConstructorTrans[language].description}</p>
                <p>
                  <strong>{WorkoutTrans[language].level}:</strong>{" "}
                  {selectedLevel.name}
                </p>
                <p>
                  <strong>{WorkoutTrans[language].type}:</strong>{" "}
                  {selectedType.name}
                </p>
                <p>
                  <strong>{WorkoutTrans[language].duration}:</strong>{" "}
                  {selectedDuration.name}
                </p>
                {selectedTags.length ? <p>
                  <strong>{ConstructorTrans[language].tags}:</strong>{" "}
                  {selectedTags
                    .map(id => tagList.find(t => t.id === id)?.label)
                    .join(", ")}
                </p> : ""}

              </motion.div>
            )}
          </AnimatePresence>

          <div className="justify-start text-center mt-5">
            <button
              onClick={isBuilt ? handleReset : handleSearch}
              className="bg-pink rounded-3xl text-white text-md py-2 px-7 hover:bg-brown w-fit mt-2 mb-2"
            >
              {isBuilt ? ButtonsTrans[language].buildAgain :
                isFilterEmpty
                  ? ButtonsTrans[language].random
                  : ButtonsTrans[language].search
              }
            </button>
          </div>

          <div className="w-full mx-auto mb-8 mt-5">
            {constructedPracticeLink && (
              <div>
                <div className="w-full aspect-video">
                  <YouTubeVideoDisplay
                    videoUrl={constructedPracticeLink}
                    onEnded={handleVideoEnd}
                  />
                </div>
                <div className="flex justify-between mt-4">
                  <button
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    className="bg-beige hover:bg-pink text-white py-0 px-4 rounded disabled:opacity-50 disabled:hover:bg-beige"
                  >
                    {ConstructorTrans[language].previous}
                  </button>
                  <button
                    onClick={handleVideoEnd}
                    disabled={currentIndex >= filteredVideos.length - 1}
                    className="bg-beige hover:bg-pink text-white px-4 py-0 rounded disabled:opacity-50 disabled:hover:bg-beige"
                  >
                    {ConstructorTrans[language].next}
                  </button>
                </div>
                {/* <div className="flex flex-col h-full md:w-8/12 mx-auto"> */}
                  {/* <div className="flex justify-between mt-2 text-xs md:text-sm"> */}
                    {/* <SaveForLaterButton
                      isSaved={selectionStates.isSaved}
                      toggleSave={() => toggleSaved(practiceUuid)}
                    /> */}
                    <div className="w-fit mx-auto ">
                      <MarkAsCompleteButton
                        isComplete={selectionStates.isComplete}
                        toggleComplete={() => toggleComplete(practiceUuid)}
                      />
                    </div>
                  {/* </div> */}
                {/* </div> */}
              </div>
              
            )}
          </div>
        </div>

        <div className="col-span-1 pt-4 hidden md:block">
          <img
            src={bgImg}
            alt="Description"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </main>
  );
}
